/**
 * Branches tab — list / add / edit / set-main / deactivate / delete.
 *
 * 2026-02-19 · Grew three owner-facing controls on top of the existing
 * CRUD: a "Main" radio column (exactly one branch is Main at a time), a
 * Deactivate / Reactivate toggle on the Edit modal, and a Delete button
 * gated by a server-side usage probe (zero patients / appointments /
 * invoices / service tickets / inventory). Everything else is left
 * untouched — staff scoping, patient data, and other settings tabs stay
 * exactly as they were.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Pencil, AlertTriangle, Check, MapPin, Star, Trash2, Power, GitMerge,
} from 'lucide-react';
import ModalShell from '../../components/ModalShell';
import { PlanUsageBar } from '../../shell/PlanUsageBar';
import MergeBranchesModal from './MergeBranchesModal';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BranchesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | branch
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [transferring, setTransferring] = useState(null); // branch_id being promoted
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      // include_inactive=1 so the owner can see & reactivate deactivated branches.
      const r = await axios.get(`${API}/branches`, { params: { include_inactive: 1 } });
      setRows(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };

  const setMain = async (branch) => {
    if (branch.is_primary) return;
    if (branch.active === false) {
      setErr('Reactivate the branch before marking it as Main.');
      return;
    }
    setTransferring(branch.branch_id); setErr('');
    try {
      await axios.post(`${API}/branches/${branch.branch_id}/set-main`);
      flash(`"${branch.name}" is now the Main branch`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not change Main branch');
    } finally {
      setTransferring(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="settings-branches-tab">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Branches</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">Physical locations for this clinic. Staff can be scoped to one or many.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMergeOpen(true)}
            disabled={rows.length < 2}
            data-testid="branch-merge-btn"
            title={rows.length < 2 ? 'Need at least two branches to merge' : 'Merge one branch into another'}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          ><GitMerge size={13} /> Merge Branches</button>
          <button
            onClick={() => setEditing('new')}
            data-testid="branch-new-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md shadow-sm"
          ><Plus size={13} /> Add Branch</button>
        </div>
      </div>

      <PlanUsageBar kind="locations" refreshKey={rows.filter((r) => r.active !== false).length} />
      {msg && <div className="mb-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="branches-flash"><Check size={13} />{msg}</div>}
      {err && <div className="mb-3 flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="branches-err"><AlertTriangle size={13} />{err}</div>}

      {loading ? (
        <div className="py-8 text-center text-slate-400 italic text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-slate-400 italic text-sm" data-testid="branches-empty">
          No branches yet. Every clinic needs at least one branch — click "Add Branch".
        </div>
      ) : (
        <div className="bg-white rounded border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-center w-[60px]">Main</th>
                <th className="px-3 py-2 text-left">Branch</th>
                <th className="px-3 py-2 text-left">City</th>
                <th className="px-3 py-2 text-left">Phone</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => {
                const isInactive = b.active === false;
                const isMain = b.is_primary === true;
                const busy = transferring === b.branch_id;
                return (
                <tr key={b.branch_id}
                    className={`border-t border-slate-100 ${isInactive ? 'bg-slate-50 text-slate-500' : ''}`}
                    data-testid={`branch-row-${b.branch_id}`}>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => setMain(b)}
                      disabled={isMain || busy || isInactive}
                      aria-pressed={isMain}
                      data-testid={`branch-main-radio-${b.branch_id}`}
                      title={isInactive ? 'Reactivate first' : isMain ? 'Already Main' : 'Set as Main'}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full border transition
                        ${isMain
                          ? 'bg-amber-50 border-amber-400 text-amber-500'
                          : isInactive
                            ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                            : 'border-slate-300 text-slate-300 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-400'}`}
                    >
                      <Star size={14} fill={isMain ? 'currentColor' : 'none'} />
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold flex items-center gap-1.5">
                      <MapPin size={11} className="text-slate-400" />
                      {b.name}
                      {isMain && (
                        <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 ml-1"
                              data-testid={`branch-main-tag-${b.branch_id}`}>· Main</span>
                      )}
                      {b.is_franchise && (
                        <span className="text-[10px] font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5 ml-1"
                              data-testid={`branch-franchise-tag-${b.branch_id}`}>Franchise</span>
                      )}
                    </div>
                    {b.address && <div className="text-[10px] text-slate-500 ml-[19px]" data-testid={`branch-row-address-${b.branch_id}`}>{b.address}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{b.city || '—'}</td>
                  <td className="px-3 py-2 text-xs">{b.phone || '—'}</td>
                  <td className="px-3 py-2 text-xs" data-testid={`branch-row-email-${b.branch_id}`}>{b.email || '—'}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-slate-500">{b.branch_id}</td>
                  <td className="px-3 py-2 text-center">
                    {!isInactive
                      ? <span className="text-[10px] font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded" data-testid={`branch-status-${b.branch_id}`}>ACTIVE</span>
                      : <span className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-200 text-slate-700 border border-slate-300 rounded" data-testid={`branch-status-${b.branch_id}`}>INACTIVE</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(b)} data-testid={`branch-edit-${b.branch_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 hover:underline"><Pencil size={11} /> Edit</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <BranchForm
          branch={editing === 'new' ? null : editing}
          totalBranches={rows.length}
          allBranches={rows}
          onMainChanged={(n) => { flash(n); load(); }}
          onClose={() => setEditing(null)}
          onSaved={(n) => { setEditing(null); flash(n); load(); }}
        />
      )}

      {mergeOpen && (
        <MergeBranchesModal
          branches={rows}
          onClose={() => setMergeOpen(false)}
          onMerged={() => { setMergeOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function BranchForm({ branch, totalBranches, allBranches, onClose, onSaved, onMainChanged }) {
  const isEdit = !!branch;
  const [f, setF] = useState({
    name: branch?.name || '',
    address: branch?.address || '',
    city: branch?.city || '',
    state: branch?.state || '',
    pincode: branch?.pincode || '',
    phone: branch?.phone || '',
    email: branch?.email || '',
    gstin: branch?.gstin || '',
    rci_registration_no: branch?.rci_registration_no || '',
    is_primary: branch?.is_primary || false,
    is_franchise: branch?.is_franchise || false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [usage, setUsage] = useState(null); // {total, patients, ...}
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  // Pull the usage probe once in edit mode so the Delete button renders
  // with the correct enabled/disabled state and we can show precise
  // counts in the warning banner.
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/branches/${branch.branch_id}/usage`);
        setUsage(r.data);
      } catch { /* non-fatal — just means Delete stays disabled */ }
    })();
  }, [isEdit, branch]);

  const save = async () => {
    if (!f.name.trim()) { setErr('Branch name is required'); return; }
    setErr(''); setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/branches/${branch.branch_id}`, f);
        onSaved('Branch updated');
      } else {
        await axios.post(`${API}/branches`, f);
        onSaved('Branch created');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleActive = async () => {
    if (!isEdit) return;
    setErr(''); setSaving(true);
    try {
      if (branch.active === false) {
        await axios.post(`${API}/branches/${branch.branch_id}/activate`);
        onSaved('Branch reactivated');
      } else {
        await axios.delete(`${API}/branches/${branch.branch_id}`);
        onSaved('Branch deactivated');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not change status');
    } finally { setSaving(false); }
  };

  const hardDelete = async () => {
    if (!isEdit) return;
    setErr(''); setSaving(true);
    try {
      await axios.delete(`${API}/branches/${branch.branch_id}`, { params: { hard: true } });
      onSaved('Branch deleted');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not delete');
      setConfirmingDelete(false);
    } finally { setSaving(false); }
  };

  const makeMain = async () => {
    setErr(''); setSaving(true);
    try {
      await axios.post(`${API}/branches/${branch.branch_id}/set-main`);
      setF((prev) => ({ ...prev, is_primary: true }));
      onMainChanged(`"${branch.name}" is now the Main branch`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not change Main branch');
    } finally { setSaving(false); }
  };

  const isMain = isEdit && f.is_primary === true;
  const canDelete = usage?.can_delete === true && !isMain && totalBranches > 1;
  const isInactive = branch?.active === false;
  const isOnlyActiveInClinic = isEdit && !isInactive && (allBranches || []).filter(
    (r) => r.clinic_id === branch.clinic_id && r.active !== false,
  ).length === 1;
  const onToggleActiveClick = () => (isOnlyActiveInClinic ? setConfirmingDeactivate(true) : toggleActive());

  return (
    <ModalShell onClose={onClose} cardClassName="max-w-lg w-full p-5" testid="branch-form-modal">
      <h3 className="text-base font-bold mb-3">{isEdit ? 'Edit Branch' : 'Add New Branch'}</h3>
      {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-2" data-testid="branch-form-err">{err}</div>}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <F label="Branch Name *"><input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="branch-field-name" placeholder="e.g. Hyderabad Central" className={inputCls} /></F>
        <F label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="branch-field-phone" className={inputCls} /></F>
        <F label="Address" className="col-span-2"><textarea rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="branch-field-address" className={inputCls} /></F>
        <F label="City"><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="branch-field-city" className={inputCls} /></F>
        <F label="State"><input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="branch-field-state" className={inputCls} /></F>
        <F label="Pincode"><input value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} data-testid="branch-field-pincode" className={inputCls} /></F>
        <F label="Email"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="branch-field-email" className={inputCls} /></F>
        <F label="GSTIN (franchise branches only)"><input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} data-testid="branch-field-gstin" placeholder="Leave blank to use the clinic GSTIN" className={inputCls} /></F>
        <F label="RCI registration no."><input value={f.rci_registration_no} onChange={(e) => setF({ ...f, rci_registration_no: e.target.value })} data-testid="branch-field-rci" placeholder="Leave blank to use the clinic RCI no." className={inputCls} /></F>
      </div>

      {isEdit && (
        <div className="mt-4 pt-3 border-t border-slate-100 space-y-3">
          <MainToggle isMain={isMain} isInactive={isInactive} saving={saving} onMakeMain={makeMain} />
          {/* Deactivate / Reactivate */}
          <div className="flex items-start justify-between gap-3 bg-slate-50 border border-slate-200 rounded p-3">
            <div className="flex-1">
              <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Power size={12} className={isInactive ? 'text-slate-400' : 'text-emerald-600'} />
                {isInactive ? 'Branch is deactivated' : 'Branch is active'}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {isInactive
                  ? 'Hidden from dropdowns. No new appointments or patients can be created here. All historical data remains accessible.'
                  : 'Deactivating hides this branch from staff dropdowns but preserves all historical records.'}
              </div>
            </div>
            <button
              onClick={onToggleActiveClick}
              disabled={saving || isMain}
              data-testid="branch-toggle-active"
              title={isMain ? 'Promote another branch to Main first' : ''}
              className={`shrink-0 px-3 py-1.5 text-[11px] font-semibold rounded shadow-sm
                ${isInactive
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300'}
                disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isInactive ? 'Reactivate Branch' : 'Deactivate Branch'}
            </button>
          </div>

          {/* Delete */}
          <div className="flex items-start justify-between gap-3 bg-rose-50/40 border border-rose-200 rounded p-3">
            <div className="flex-1">
              <div className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                <Trash2 size={12} /> Delete this branch
              </div>
              <div className="text-[10px] text-rose-900/80 mt-0.5" data-testid="branch-delete-note">
                {isMain
                  ? 'This is the Main branch — promote another branch to Main first.'
                  : totalBranches <= 1
                    ? 'Cannot delete the only branch in the clinic.'
                    : canDelete
                      ? 'This branch has zero records — it can be permanently removed.'
                      : usage
                        ? `This branch has existing data (${usage.patients} patients · ${usage.appointments} appointments · ${usage.invoices} invoices · ${usage.service_tickets} service jobs · ${usage.inventory} inventory items) and cannot be deleted. You can deactivate it instead.`
                        : 'Checking data…'}
              </div>
            </div>
            <button
              onClick={() => setConfirmingDelete(true)}
              disabled={!canDelete || saving}
              data-testid="branch-delete-open"
              className="shrink-0 px-3 py-1.5 text-[11px] font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Delete Branch
            </button>
          </div>
        </div>
      )}

      <label className="mt-3 flex items-start gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="branch-franchise-label">
        <Checkbox
          checked={f.is_franchise}
          onCheckedChange={(v) => setF({ ...f, is_franchise: v === true })}
          data-testid="branch-franchise-checkbox"
        />
        <span>
          Franchise branch (co-owner)
          <span className="block text-[11px] text-slate-500">Documents print this branch's own phone, email and GSTIN. Owner-run branches print the main clinic's phone / email alongside the branch's.</span>
        </span>
      </label>

      {!isEdit && (
        <label className="mt-3 flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="branch-new-main-label">
          <Checkbox
            checked={f.is_primary}
            onCheckedChange={(v) => setF({ ...f, is_primary: v === true })}
            data-testid="branch-new-set-main-checkbox"
          />
          Set as Main branch
        </label>
      )}

      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
        <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="branch-save" className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded shadow-sm">
          {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create Branch')}
        </button>
      </div>

      {confirmingDeactivate && (
        <ModalShell onClose={() => setConfirmingDeactivate(false)} cardClassName="max-w-sm w-full p-5" testid="branch-deactivate-confirm">
          <h4 className="text-sm font-bold text-amber-700 flex items-center gap-1.5"><AlertTriangle size={14} /> Deactivate the only branch?</h4>
          <p className="text-xs text-slate-700 mt-2" data-testid="branch-deactivate-warning">
            This is the only active branch of <b>{branch?.clinic_name || branch?.name}</b>. Once deactivated, staff at that clinic will have no branch to pick, and they won't be able to create appointments, patients or invoices there. Old records stay safe.
          </p>
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <button onClick={() => setConfirmingDeactivate(false)} data-testid="branch-deactivate-cancel-btn" className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
            <button onClick={() => { setConfirmingDeactivate(false); toggleActive(); }} disabled={saving} data-testid="branch-deactivate-confirm-btn"
              className="px-3 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded shadow-sm disabled:opacity-50">
              Yes, deactivate
            </button>
          </div>
        </ModalShell>
      )}

      {confirmingDelete && (
        <ModalShell onClose={() => setConfirmingDelete(false)} cardClassName="max-w-sm w-full p-5" testid="branch-delete-confirm">
          <h4 className="text-sm font-bold text-rose-700 flex items-center gap-1.5"><AlertTriangle size={14} /> Confirm deletion</h4>
          <p className="text-xs text-slate-700 mt-2">
            This will permanently delete <b>{branch?.name}</b>. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <button onClick={() => setConfirmingDelete(false)} className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
            <button onClick={hardDelete} disabled={saving} data-testid="branch-delete-confirm-btn"
              className="px-3 py-1.5 text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded shadow-sm disabled:opacity-50">
              {saving ? 'Deleting…' : 'Yes, delete permanently'}
            </button>
          </div>
        </ModalShell>
      )}
    </ModalShell>
  );
}

function MainToggle({ isMain, isInactive, saving, onMakeMain }) {
  const disabled = isMain || isInactive || saving;
  return (
    <div className={`flex items-start justify-between gap-3 border rounded p-3 ${isMain ? 'bg-amber-50/60 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
         data-testid="branch-main-toggle-section">
      <div className="flex-1">
        <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5" data-testid="branch-main-toggle-label">
          <Star size={12} className={isMain ? 'text-amber-500' : 'text-slate-400'} fill={isMain ? 'currentColor' : 'none'} />
          {isMain ? 'This is the Main branch' : 'Set as Main branch'}
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {isMain
            ? 'To change the Main branch, open another branch and set it as Main.'
            : isInactive
              ? 'Reactivate this branch before marking it as Main.'
              : 'Turning this on moves the Main designation here immediately.'}
        </div>
      </div>
      <Switch
        checked={isMain}
        disabled={disabled}
        onCheckedChange={(v) => { if (v) onMakeMain(); }}
        data-testid="branch-main-toggle"
        className="mt-0.5 data-[state=checked]:bg-amber-500"
      />
    </div>
  );
}

const inputCls = 'w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-indigo-400';
const F = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">{label}</span>
    {children}
  </label>
);
